import { HealthHistoryQuestionnaireKey } from './health-history';

export enum HealthHistorySections {
  MyGoals = 'MyGoals',
  AboutYou = 'AboutYou',
  Lifestyle = 'Lifestyle',
  DiseaseRecognition = 'DiseaseRecognition',
  HealthConcerns = 'HealthConcerns',
  AllergiesIntolerances = 'AllergiesIntolerances',
  MedicationsSupplements = 'MedicationsSupplements'
}

export type HealthHistorySectionIds =
  | HealthHistorySections.MyGoals
  | HealthHistorySections.AboutYou
  | HealthHistorySections.Lifestyle
  | HealthHistorySections.DiseaseRecognition
  | HealthHistorySections.HealthConcerns
  | HealthHistorySections.AllergiesIntolerances
  | HealthHistorySections.MedicationsSupplements;

export interface HealthHistorySection {
  id: HealthHistorySectionIds;
  image: string;
  title: string;
  description: string;
  pageDescription?: string;
  timeLow: number;
  timeHigh: number;
  required: boolean;
  questions: HealthHistoryQuestionnaireKey[];
  percentComplete: number;
  complete: boolean;
}

export const healthHistoryAboutYouSection: HealthHistorySection = {
  id: HealthHistorySections.AboutYou,
  image: 'assets/img/health-history/about-you.png',
  title: 'Health_History.About_You.Title',
  description: 'Health_History.About_You.Description',
  timeLow: 2,
  timeHigh: 4,
  required: true,
  percentComplete: 0,
  complete: false,
  questions: [
    HealthHistoryQuestionnaireKey.Birthday,
    HealthHistoryQuestionnaireKey.GeneticSex,
    HealthHistoryQuestionnaireKey.PregnantLactating,
    HealthHistoryQuestionnaireKey.Height,
    HealthHistoryQuestionnaireKey.Weight,
    HealthHistoryQuestionnaireKey.WaistCircumference,
    HealthHistoryQuestionnaireKey.DailyActivityLevel,
    HealthHistoryQuestionnaireKey.SatisfiedWithWeight,
    HealthHistoryQuestionnaireKey.Ethnicity,
    HealthHistoryQuestionnaireKey.FeltEnergyLevel,
    HealthHistoryQuestionnaireKey.FeltPain,
    HealthHistoryQuestionnaireKey.FeltHappiness,
    HealthHistoryQuestionnaireKey.FeltThinking,
    HealthHistoryQuestionnaireKey.FeltContentment,
    HealthHistoryQuestionnaireKey.FeltMemory,
    HealthHistoryQuestionnaireKey.FeltEating,
    HealthHistoryQuestionnaireKey.FeltSleep,
    HealthHistoryQuestionnaireKey.FeltBodyShape,
    HealthHistoryQuestionnaireKey.FeltTummy,
    HealthHistoryQuestionnaireKey.FeltBowelMovement,
    HealthHistoryQuestionnaireKey.FeltPurpose,
    HealthHistoryQuestionnaireKey.FeltSelfCare,
    HealthHistoryQuestionnaireKey.FeltRelationships,
    HealthHistoryQuestionnaireKey.FeltSpiritually,
    HealthHistoryQuestionnaireKey.FeltMotivation
  ]
};

export const healthHistoryLifestyleSection: HealthHistorySection = {
  id: HealthHistorySections.Lifestyle,
  image: 'assets/img/health-history/lifestyle.png',
  title: 'Health_History.Lifestyle.Title',
  description: 'Health_History.Lifestyle.Description',
  timeLow: 2,
  timeHigh: 4,
  required: true,
  percentComplete: 0,
  complete: false,
  questions: [
    HealthHistoryQuestionnaireKey.PhysicalActivityTimeSpent,
    HealthHistoryQuestionnaireKey.PhysicalActivityType,
    HealthHistoryQuestionnaireKey.HoursOfSleep,
    HealthHistoryQuestionnaireKey.GlassesOfWater,
    HealthHistoryQuestionnaireKey.StressWork,
    HealthHistoryQuestionnaireKey.StressFamily,
    HealthHistoryQuestionnaireKey.StressSocial,
    HealthHistoryQuestionnaireKey.StressFinancial,
    HealthHistoryQuestionnaireKey.StressHealth,
    HealthHistoryQuestionnaireKey.StressOther,
    HealthHistoryQuestionnaireKey.EatOut,
    HealthHistoryQuestionnaireKey.DietIsHealthy,
    HealthHistoryQuestionnaireKey.EatBreakfast,
    HealthHistoryQuestionnaireKey.EatHealthySnacks,
    HealthHistoryQuestionnaireKey.NeverEatLate,
    HealthHistoryQuestionnaireKey.AdoptHealthierLifestyle,
    HealthHistoryQuestionnaireKey.StressImpactAppetite,
    HealthHistoryQuestionnaireKey.FoodCravings,
    HealthHistoryQuestionnaireKey.FeelEnergized,
    HealthHistoryQuestionnaireKey.SugaryDrinks,
    HealthHistoryQuestionnaireKey.AlcoholUse,
    HealthHistoryQuestionnaireKey.TobaccoUse,
    HealthHistoryQuestionnaireKey.DrugUse,
    HealthHistoryQuestionnaireKey.ToxinConcern
  ]
};

export const healthHistoryDiseaseRecognitionSection: HealthHistorySection = {
  id: HealthHistorySections.DiseaseRecognition,
  image: 'assets/img/health-history/disease-recognition.png',
  title: 'Health_History.Disease_Recognition.Title',
  description: 'Health_History.Disease_Recognition.Description',
  pageDescription: 'Health_History.Disease_Recognition.Page_Description',
  timeLow: 2,
  timeHigh: 4,
  required: true,
  percentComplete: 0,
  complete: false,
  questions: [
    HealthHistoryQuestionnaireKey.ConditionAutoImmune,
    HealthHistoryQuestionnaireKey.ConditionBowelOstomy,
    HealthHistoryQuestionnaireKey.ConditionCancer,
    HealthHistoryQuestionnaireKey.ConditionCeliac,
    HealthHistoryQuestionnaireKey.ConditionCopd,
    HealthHistoryQuestionnaireKey.ConditionChf,
    HealthHistoryQuestionnaireKey.ConditionConstipation,
    HealthHistoryQuestionnaireKey.ConditionCoronaryArtery,
    HealthHistoryQuestionnaireKey.ConditionCrohns,
    HealthHistoryQuestionnaireKey.ConditionCysticFibrosis,
    HealthHistoryQuestionnaireKey.ConditionDimentiaAlzeimerParkinson,
    HealthHistoryQuestionnaireKey.ConditionDiabetes,
    HealthHistoryQuestionnaireKey.ConditionDiarrhea,
    HealthHistoryQuestionnaireKey.ConditionDiverticulitis,
    HealthHistoryQuestionnaireKey.ConditionEatingDisorder,
    HealthHistoryQuestionnaireKey.ConditionGerd,
    HealthHistoryQuestionnaireKey.ConditionGastroparesis,
    HealthHistoryQuestionnaireKey.ConditionGout,
    HealthHistoryQuestionnaireKey.ConditionHighBloodPressure,
    HealthHistoryQuestionnaireKey.ConditionHighCholesterol,
    HealthHistoryQuestionnaireKey.ConditionHivImmuneSuppression,
    HealthHistoryQuestionnaireKey.ConditionIbs,
    HealthHistoryQuestionnaireKey.ConditionKidneyDisease,
    HealthHistoryQuestionnaireKey.ConditionOrganTransplant,
    HealthHistoryQuestionnaireKey.ConditionOsteoporosis,
    HealthHistoryQuestionnaireKey.ConditionOverweightObesity,
    HealthHistoryQuestionnaireKey.ConditionPancreatitis,
    HealthHistoryQuestionnaireKey.ConditionPepticUlcer,
    HealthHistoryQuestionnaireKey.ConditionTroubleChewingSwallowing,
    HealthHistoryQuestionnaireKey.ConditionOther,
    HealthHistoryQuestionnaireKey.ConditionNone,
    HealthHistoryQuestionnaireKey.PrescribedDiet,
    HealthHistoryQuestionnaireKey.PrescribedDietDescription
  ]
};

export const healthHistoryHealthConcernsSection: HealthHistorySection = {
  id: HealthHistorySections.HealthConcerns,
  image: 'assets/img/health-history/health-concerns.png',
  title: 'Health_History.Health_Concerns.Title',
  description: 'Health_History.Health_Concerns.Description',
  pageDescription: 'Health_History.Health_Concerns.Page_Description',
  timeLow: 2,
  timeHigh: 4,
  required: true,
  percentComplete: 0,
  complete: false,
  questions: [
    HealthHistoryQuestionnaireKey.HealthConcernPhysicalActivity,
    HealthHistoryQuestionnaireKey.HealthConcernNutrition,
    HealthHistoryQuestionnaireKey.HealthConcernOverweight,
    HealthHistoryQuestionnaireKey.HealthConcernTobacco,
    HealthHistoryQuestionnaireKey.HealthConcernSubstanceAbuse,
    HealthHistoryQuestionnaireKey.HealthConcernMentalHealth,
    HealthHistoryQuestionnaireKey.HealthConcernInjuryViolence,
    HealthHistoryQuestionnaireKey.HealthConcernEnvironmentalQuality,
    HealthHistoryQuestionnaireKey.HealthConcernImmunization,
    HealthHistoryQuestionnaireKey.HealthConcernAccessToHealthcare,
    HealthHistoryQuestionnaireKey.HowOftenSick,
    HealthHistoryQuestionnaireKey.EnoughEnergyForDailyActivities,
    HealthHistoryQuestionnaireKey.MotivationForGoals,
    HealthHistoryQuestionnaireKey.DietaryGoals
  ]
};

export const healthHistoryAllergiesIntolerancesSection: HealthHistorySection = {
  id: HealthHistorySections.AllergiesIntolerances,
  image: 'assets/img/health-history/allergies-intolerances.png',
  title: 'Health_History.Allergies_Intolerances.Title',
  description: 'Health_History.Allergies_Intolerances.Description',
  timeLow: 2,
  timeHigh: 4,
  required: true,
  percentComplete: 0,
  complete: false,
  questions: [HealthHistoryQuestionnaireKey.Allergens, HealthHistoryQuestionnaireKey.Intolerances]
};

export const healthHistoryMedicationsSupplementsSection: HealthHistorySection = {
  id: HealthHistorySections.MedicationsSupplements,
  image: 'assets/img/health-history/medications-supplements.png',
  title: 'Health_History.Medications_Supplements.Title',
  description: 'Health_History.Medications_Supplements.Description',
  timeLow: 2,
  timeHigh: 4,
  required: true,
  percentComplete: 0,
  complete: false,
  questions: [HealthHistoryQuestionnaireKey.Medications, HealthHistoryQuestionnaireKey.Supplements]
};

export const healthHistorySections: HealthHistorySection[] = [
  healthHistoryAboutYouSection,
  healthHistoryLifestyleSection,
  healthHistoryDiseaseRecognitionSection,
  healthHistoryHealthConcernsSection,
  healthHistoryAllergiesIntolerancesSection,
  healthHistoryMedicationsSupplementsSection
];

export const healthHistorySectionsMap: { [key: string]: HealthHistorySection } = {
  [HealthHistorySections.AboutYou]: healthHistoryAboutYouSection,
  [HealthHistorySections.Lifestyle]: healthHistoryLifestyleSection,
  [HealthHistorySections.DiseaseRecognition]: healthHistoryDiseaseRecognitionSection,
  [HealthHistorySections.HealthConcerns]: healthHistoryHealthConcernsSection,
  [HealthHistorySections.AllergiesIntolerances]: healthHistoryAllergiesIntolerancesSection,
  [HealthHistorySections.MedicationsSupplements]: healthHistoryHealthConcernsSection
};
